import { Inject, Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { Distributionanalyticsv1PortfolioAnalyticsResponse } from '@blk/portfolio-security-server/analytics/portfolio-analytics';
import { WweInputTotalPortfolio } from '@blk/wealth-web-endpoint';
import { ModelsEntitlementService, PortfolioAnalysisErrors } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { ConfigService, SharedConfigKey } from '@ddt-web/config-layer';
import { AnalysisParams, ApiService, ApiSettings, ApiStoreConfiguration, AppStoreConfig, PasRcResolverService, PortfolioAnalysisOptions, WwePortfolioAnalysisServiceParams } from '@ddt-web/data-service';
import { ErrorObject, PerformanceDateMisalignedError } from '@ddt-web/error-handler';
import { SecurityIdentifierUtil } from '@ddt-web/generic-utils';
import { PasApiService } from '@ddt-web/pas';
import { StoreService } from '@ddt-web/store-service';
import { WealthWebApiParamUtil } from '@ddt-web/wealth-web';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as compact from 'lodash/compact';
import * as min from 'lodash/min';
import * as uniq from 'lodash/uniq';
import { Observable } from 'rxjs';
import { catchError, first, withLatestFrom } from 'rxjs/operators';

import { PasDataPortfolioAnalysisService } from './pas-data-portfolio-analysis.service';

/**
 * This service is used for analyzing multiple portfolios with different benchmarks
 */
@Injectable()
@UntilDestroy()
export class PasDataMultiPortfolioAnalysisService extends PasDataPortfolioAnalysisService {
	private readonly ALL_ANALYSIS_CONFIG_NAMES = ['risk', 'details', 'performance'];

	constructor(
		apiService: ApiService,
		storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) protected storeConfig: AppStoreConfig,
		contentService: ContentService,
		modelsEntitlementService: ModelsEntitlementService,
		protected pasApiService: PasApiService,
		protected configService: ConfigService,
		protected pasRcResolver: PasRcResolverService
	) {
		super(storeConfig, storeService, contentService, pasApiService, configService, pasRcResolver, modelsEntitlementService, apiService);
	}

	/**
	 * @description loadDefaultStoreData: performs the appropriate api calls and updates the corresponding stores.
	 * @param {apiSettings} apiSettings: api specific settings: rcs, config, and portfolioAnalysisParams.
	 * @param {AnalysisParams} analysisParams: the params used to build the api call.
	 */
	public async loadDefaultStoreData(apiSettings: ApiSettings, analysisParams: any): Promise<void> {
		const failedPortfolios = new Set<string>();

		// Get RC and API params for every config provided
		const apiSettingsGroupedByPortfolioAndRc = {};
		for (const config of apiSettings.configs) {
			for (const analysisParam of analysisParams.analysisParamsArray) {
				const useAladdinId = this.configService.get(SharedConfigKey.AWE_USE_ALADDINID, false);
				const portfolios = WealthWebApiParamUtil.createPortfolioParams(analysisParam?.analysisParamsArray, useAladdinId);
				const benchmark = WealthWebApiParamUtil.createBenchmarkParam(analysisParam, useAladdinId);
				if (this.isValidAnalysisData(portfolios)) {
					const rc = await this.pasRcResolver.getRcForUserType(config.name).toPromise();
					const apiParamsSettings = {
						config,
						rcs: '',
						portfolioAnalysisParams: null,
						wwePortfolioAnalysisParams: {
							body: {
								portfolios,
								benchmark: config.name === 'performanceExBenchmark' ? null : benchmark,
								simulatedPerformanceUserInputs: analysisParam?.simulatedPerformanceUserInputs
							} as WweInputTotalPortfolio,
							portfolios: analysisParam.analysisParamsArray,
							rc
						} as WwePortfolioAnalysisServiceParams
					} as ApiSettings;

					const portfolioId = portfolios[0].id;

					const updatedArraySettings = apiSettingsGroupedByPortfolioAndRc[portfolioId + '-' + rc] ?? [];
					updatedArraySettings.push(apiParamsSettings);
					apiSettingsGroupedByPortfolioAndRc[portfolioId + '-' + rc] = updatedArraySettings;
				} else {
					portfolios.forEach(portfolio => failedPortfolios.add(portfolio.id));
				}
			}
		}

		// Make the API call
		const apiResponses = await Promise.allSettled(
			Object.values(apiSettingsGroupedByPortfolioAndRc)
				.map(apiSettings => {
					return this.dispatchFetchAwaPortfolioAnalysisApiCall(apiSettings[0]).toPromise();
				})
		);

		// Process success and other errors
		const errorsByPortfolioId: { [key: string]: any[] } = {};
		for (const [index, apiSettings] of Object.values(apiSettingsGroupedByPortfolioAndRc).entries()) {
			for (const apiSetting of Object.values(apiSettings)) {
				const apiResponse: PromiseSettledResult<any> = apiResponses[index];
				const portfolioId = apiSetting.wwePortfolioAnalysisParams.portfolios[0].uniqueId;
				const configName = apiSetting.config.name;
				const portfolioAnalytics = apiResponse?.['value']?.['portfolioAnalytics'];

				if (apiResponse.status === 'fulfilled' && portfolioAnalytics) {
					this.processSuccessfulAnalysis(portfolioAnalytics, apiSetting);
					if (configName === 'performance' || configName === 'performanceExBenchmark') {
						this.checkPerformanceDate(portfolioAnalytics);
					}
				} else {
					const errorResponse = apiResponse.status === 'fulfilled' ? apiResponse?.['value'] : apiResponse?.['reason'];
					const portfolioErrors = errorsByPortfolioId[portfolioId] ?? [];
					portfolioErrors.push({ ...errorResponse, configName });
					errorsByPortfolioId[portfolioId] = portfolioErrors;
				}
			}
		}

		// ---- ERROR HANDLING
		for (const [portfolioId, errorResponse] of Object.entries(errorsByPortfolioId)) {
			const errorConfigNames = errorResponse.map(error => error.configName);
			if (this.ALL_ANALYSIS_CONFIG_NAMES.every(name => errorConfigNames.includes(name))) {
				failedPortfolios.add(portfolioId);
			}
		}

		if (failedPortfolios.size) {
			apiSettings.configs.forEach(
				config => this.storeService.upsertMany(config, Array.from(failedPortfolios).map(id => ({ id, error: { errorCode: PortfolioAnalysisErrors.PARTIAL_ERROR } })))
			);
		}
	}

	protected dispatchFetchAwaPortfolioAnalysisApiCall(apiSettings: ApiSettings): Observable<Distributionanalyticsv1PortfolioAnalyticsResponse> {
		// Create the observable which handles critical errors
		return this.pasApiService.fetchPortfolioAnalysis(apiSettings.wwePortfolioAnalysisParams).pipe(
			first(),
			catchError((error) => {
				Object.assign(error, { 'portfolioUniqueId': apiSettings?.wwePortfolioAnalysisParams?.portfolios[0]?.uniqueId });
				throw new ErrorObject(error, apiSettings.config.name);
			})
		);
	}

	protected dispatchAllDataLoaders(
		configs: Array<ApiStoreConfiguration>,
		generalAnalysisParams: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		const apiSettings: ApiSettings = {
			portfolioAnalysisParams: null,
			configs: configs
		};
		configs.forEach(config => this.storeService.fetch(config));
		this.dispatchDataLoaders(apiSettings, generalAnalysisParams, portfolioAnalysisOptions);
	}

	protected checkPerformanceDate(resultList: any[]): void {
		if (resultList.length > 1) {
			const dates = resultList.map(res => res.totalPortfolioAnalysis.portfolioAnalysis.performance?.exPostMetrics[0]?.interval?.stopDate);
			if (compact(uniq(dates)).length > 1) {
				this.storeService.selectAll(this.storeConfig.positionDetails)
					.pipe(withLatestFrom(this.storeService.selectAll(this.storeConfig.associatedPortfolios)), untilDestroyed(this))
					.subscribe(([positionDetails, associatedPortfolios]) => {
						const securitiesByPortfolio = new Map<string, any>();
						const portfolioIds: any[] = [];
						const oldDate = min(dates);
						const securityDateRanges: any[][] = resultList.map(res => res.totalPortfolioAnalysis.portfolioAnalysis.performance?.assetPerformanceDateRanges);
						securityDateRanges.forEach((securityDateRange, idx) => {
							const securities = positionDetails.entities[resultList[idx].totalPortfolioAnalysis.id]?.children;
							if (securityDateRange && securities?.length) {
								const portfolio = associatedPortfolios.entities[resultList[idx].totalPortfolioAnalysis.id];
								const description = portfolio.description;
								const oldSecurities = securityDateRange.filter(security => security?.interval?.stopDate === oldDate);
								const resolvedSecurities = oldSecurities.map(security =>
									securities.find(s => SecurityIdentifierUtil.buildSecurityAggregateKey(s) === security.id)
								);
								if (compact(resolvedSecurities).length) {
									portfolioIds.push(resultList[idx].totalPortfolioAnalysis.id);
									securitiesByPortfolio.set(description, resolvedSecurities);
								}
							}
						});
						if (securitiesByPortfolio.size) {
							throw new PerformanceDateMisalignedError(portfolioIds, securitiesByPortfolio);
						}
					});
			}
		}
	}
}
